import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import { SHAREPOINT_PORTAL_FLAG_KEY } from 'components/SideNav/link-data'
import useGlobalFlag from 'hooks/useGlobalFlag'

/**
 * Access gate for /sharepoint.
 *
 * Only platform admins may access the page, and only while the EPS GLOBAL flag
 * `admin.sharepoint.enable-portal` is on (Settings > Feature Flags). Everyone
 * else is redirected to Home.
 *
 * Rendering is deferred until the role (and, for platform admins, the flag) is
 * known — this prevents a brief flash of the page before the redirect fires.
 * Children (and their data-fetching effects) only mount after the gate passes.
 */
export const SharePointRouteGate = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const role = useSelector(authSelectors.getRole) as string | undefined
  const isPlatformAdmin = role === ROLE_PLATFORM_HIXME_ADMIN
  const { isEnabled, isLoading } = useGlobalFlag(
    isPlatformAdmin ? SHAREPOINT_PORTAL_FLAG_KEY : ''
  )

  const isReady = Boolean(role) && (!isPlatformAdmin || !isLoading)
  const isAllowed = isPlatformAdmin && isEnabled

  useEffect(() => {
    if (isReady && !isAllowed) browserHistory.replace('/')
  }, [isReady, isAllowed])

  if (!isReady || !isAllowed) return null
  return <>{children}</>
}
